import React from 'react';
import { Phone, Mail, MapPin, Clock, Calendar, Navigation, PawPrint } from 'lucide-react';
import { Link } from 'react-router-dom';
import Logo from './Logo';
import { BUSINESS, SERVICES } from '../lib/data';

const POLICIES = [
  { label: 'Contact Us', to: '/contact' },
  { label: 'New Client Form', to: '/intake-form' },
  { label: 'Privacy Policy', to: '/privacy-policy' },
  { label: 'Terms of Service', to: '/terms-of-service' },
  { label: 'Cancellation Policy', to: '/cancellation-policy' },
];

export default function Footer() {
  return (
    <footer className="relative bg-ink-900 text-white mt-16 overflow-hidden grain">
      {/* brand tape */}
      <div aria-hidden className="h-1.5 bg-gradient-to-r from-pink-500 via-purple-500 to-cyan-500" />
      {/* ambient glows */}
      <div aria-hidden className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-pink-500/15 blur-3xl" />
      <div aria-hidden className="absolute -bottom-32 -right-24 w-[28rem] h-[28rem] rounded-full bg-cyan-500/15 blur-3xl" />

      {/* finale + conversion row */}
      <div className="relative max-w-7xl mx-auto px-4 pt-16 pb-6 text-center">
        <p className="font-display font-black italic leading-none text-[12vw] md:text-[7.5vw] bg-gradient-to-r from-pink-500 via-purple-400 to-cyan-400 bg-clip-text text-transparent select-none">
          Stay pawsome.
        </p>
        <p className="mt-5 text-white/60 max-w-xl mx-auto">
          One spa day and they'll never look at the garden hose the same way again.
        </p>
        <div className="mt-7 flex flex-col sm:flex-row items-center justify-center gap-4">
          <Link
            to="/booking"
            className="btn-shine inline-flex items-center space-x-2.5 bg-gradient-to-r from-pink-500 to-cyan-500 text-white px-8 py-4 rounded-full font-extrabold shadow-2xl shadow-pink-500/20 transition-transform duration-300 hover:scale-[1.04]"
          >
            <Calendar className="w-5 h-5" />
            <span>Book a Spa Day</span>
          </Link>
          <a
            href={BUSINESS.phoneHref}
            className="inline-flex items-center space-x-2.5 bg-white/10 hover:bg-white/20 backdrop-blur border border-white/25 px-8 py-4 rounded-full font-extrabold transition-colors"
          >
            <Phone className="w-5 h-5 text-pink-400" />
            <span>{BUSINESS.phone}</span>
          </a>
        </div>
      </div>

      <div className="relative max-w-7xl mx-auto px-4 py-12">
        <div className="grid gap-10 md:gap-8 md:grid-cols-4">
          {/* Brand */}
          <div>
            <Link to="/" className="flex items-center space-x-3 mb-4 hover:opacity-90 transition-opacity">
              <Logo size="header" className="ring-2 ring-white/20" />
              <h3 className="font-display text-2xl font-black">Bubbles &amp; Bark</h3>
            </Link>
            <p className="text-white/55 text-sm leading-relaxed">
              Premium pet grooming, spa and bakery in Orlando. Your pet's comfort and
              well-being is our whole job.
            </p>
            <div className="mt-4 flex items-center space-x-1.5 text-xs font-extrabold uppercase tracking-widest text-white/35">
              <PawPrint className="w-3.5 h-3.5 text-pink-400" />
              <span>Dogs &amp; cats · Spa &amp; bakery</span>
            </div>
          </div>

          {/* Services — real links now */}
          <div>
            <h4 className="text-xs font-extrabold uppercase tracking-[0.22em] bg-gradient-to-r from-pink-400 to-cyan-400 bg-clip-text text-transparent mb-5">Services</h4>
            <ul className="space-y-2.5 text-sm">
              {SERVICES.map((s) => (
                <li key={s.key}>
                  <Link to="/#services" className="text-white/60 hover:text-white hover:translate-x-1 inline-block transition-all">
                    {s.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact — clickable */}
          <div>
            <h4 className="text-xs font-extrabold uppercase tracking-[0.22em] bg-gradient-to-r from-pink-400 to-cyan-400 bg-clip-text text-transparent mb-5">Contact</h4>
            <div className="space-y-3.5 text-sm">
              <a href={BUSINESS.phoneHref} className="flex items-center space-x-2.5 text-white/60 hover:text-white transition-colors">
                <Phone className="w-4 h-4 text-pink-400 shrink-0" />
                <span>{BUSINESS.phone}</span>
              </a>
              <a href={`mailto:${BUSINESS.email}`} className="flex items-center space-x-2.5 text-white/60 hover:text-white transition-colors">
                <Mail className="w-4 h-4 text-pink-400 shrink-0" />
                <span>{BUSINESS.email}</span>
              </a>
              <a href={BUSINESS.mapsUrl} target="_blank" rel="noreferrer" className="flex items-start space-x-2.5 text-white/60 hover:text-white transition-colors">
                <MapPin className="w-4 h-4 text-pink-400 shrink-0 mt-0.5" />
                <span>{BUSINESS.address}</span>
              </a>
              <a href={BUSINESS.mapsUrl} target="_blank" rel="noreferrer" className="inline-flex items-center space-x-1.5 text-cyan-400 hover:text-cyan-300 font-extrabold transition-colors">
                <Navigation className="w-3.5 h-3.5" />
                <span>Get directions</span>
              </a>
            </div>
          </div>

          {/* Hours */}
          <div>
            <h4 className="text-xs font-extrabold uppercase tracking-[0.22em] bg-gradient-to-r from-pink-400 to-cyan-400 bg-clip-text text-transparent mb-5">Hours</h4>
            <div className="space-y-2.5 text-sm">
              {BUSINESS.hours.map((h) => (
                <div key={h.days} className="flex items-center justify-between gap-3 text-white/60">
                  <span className="flex items-center space-x-2"><Clock className="w-3.5 h-3.5 text-pink-400" /><span>{h.days}</span></span>
                  <span className="font-bold text-white/80 whitespace-nowrap">{h.time}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom */}
        <div className="border-t border-white/10 mt-12 pt-7 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-white/40 text-sm">
            © {new Date().getFullYear()} Bubbles &amp; Bark. All rights reserved.
          </p>
          <div className="flex flex-wrap justify-center gap-x-6 gap-y-2">
            {POLICIES.map((p) => (
              <Link key={p.label} to={p.to} className="text-white/40 hover:text-pink-300 text-sm transition-colors">
                {p.label}
              </Link>
            ))}
          </div>
        </div>
        <p className="text-center text-white/25 text-xs mt-6">
          Powered by <a href="https://dmarketingsharks.com/continue" target="_blank" rel="noreferrer" className="text-white/40 hover:text-white/70 transition-colors">DMS</a>
        </p>
      </div>
    </footer>
  );
}
